import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquare } from '@fortawesome/pro-solid-svg-icons';

// Utils
import cn from '../../../utils/cn';

// Components
import { Popover } from '../../../store/popover/PopoverContext';
import { PopoverTrigger, PopoverContent } from '../../atoms/Popover/Popover';
import Picto from '../../atoms/Picto/Picto';
import Checkbox from '../../atoms/Checkbox/Checkbox';
import SearchField from '../SearchField/SearchField';
import ColumnsVisibility from '../ColumnsVisibility/ColumnsVisibility';
import SearchGroupList, { SearchGroupListLabel } from '../SearchGroupList/SearchGroupList';
import SearchListItem from '../SearchListItem/SearchListItem';

// Styles
import styles from './SelectMultipleCheckboxList.module.css';
import useSelectMultipleCheckboxList from '../../../domain/selectMultipleCheckboxList/useSelectMultipleCheckboxList';

/**
 * SelectMultipleCheckboxList - Multi-select with trigger (Select-like) opening a popover of checkboxes
 * @param {object} props
 * @param {Array<{ label: string, options: Array<{ value: string, label: string, color?: string }> }>} props.optionsGrouped
 * @param {string[]} props.value - Selected option IDs
 * @param {(ids: string[]) => void} props.onChange
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.error]
 * @param {string} [props.id]
 * @returns {JSX.Element}
 */
function SelectMultipleCheckboxList({
  optionsGrouped,
  value = [],
  onChange,
  disabled = false,
  error = '',
  id: propId = 'select-multiple-checkbox',
}) {
  const { t } = useTranslation();

  const {
    search,
    setSearch,
    popoverOpen,
    setPopoverOpen,
    optionsLabelMap,
    filteredOptionsGrouped,
    areAllVisibleSelected,
    selectAllVisibleIndeterminate,
    handleSelectAllVisible,
    handleSelectGroup,
    areAllGroupSelected,
    isGroupOptionsIndeterminate,
    handleToggleOption,
    selectedSet,
  } = useSelectMultipleCheckboxList({
    optionsGrouped,
    value,
    onChange,
  });

  const placeholder = t('table.filters.selectTags');
  const selectAllLabel = t('table.filters.selectAllM');
  const messageNoOptions = t('table.filters.noResult');
  const searchPlaceholder = t('table.filters.columnsGroupsSearch');

  let triggerDisplay;
  if (!value?.length) {
    triggerDisplay = <span className={cn([styles['trigger-label'], styles.placeholder])}>{placeholder}</span>;
  } else if (value.length === 1) {
    const firstId = String(value[0]);
    const info = optionsLabelMap[firstId];
    const label = info?.label ?? firstId;
    const color = info?.color;
    const hasColor = color && color !== 'transparent';
    triggerDisplay = (
      <span className={styles['trigger-single-value']}>
        {hasColor && (
          <FontAwesomeIcon
            icon={faSquare}
            className={styles['trigger-color-icon']}
            style={{ color }}
            aria-label={`Couleur: ${color}`}
          />
        )}
        <span className={styles['trigger-label']}>{label}</span>
      </span>
    );
  } else {
    triggerDisplay = (
      <span className={styles['trigger-label']}>
        {t('table.filters.nChoices', { count: value.length })}
      </span>
    );
  }

  return (
    <div
      className={cn([styles.container, error && styles['has-error']])}
      id={propId ? `${propId}-wrapper` : undefined}
    >
      <Popover
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        placement="bottom-start"
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn([styles.trigger, disabled && styles.disabled])}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={popoverOpen}
            aria-describedby={error ? `${propId}-error` : undefined}
            onClick={() => setPopoverOpen(!popoverOpen)}
          >
            {triggerDisplay}
            <Picto
              icon={popoverOpen ? 'unfold-less' : 'unfold-more'}
              className={styles.picto}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent>
          <div className={styles['popover-content']}>
            <div className={styles['search-wrap']}>
              <SearchField
                id={propId ? `${propId}-search` : 'select-multiple-search'}
                placeholder={searchPlaceholder}
                onFocus={() => {}}
                disabled={false}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className={styles['select-all-wrap']}>
              <Checkbox
                id={propId ? `${propId}-select-all` : 'select-all'}
                checked={areAllVisibleSelected}
                indeterminate={selectAllVisibleIndeterminate}
                onChange={handleSelectAllVisible}
                size="small"
                theme="black"
                label={selectAllLabel}
              />
            </div>
            <div className={styles['list-wrap']}>
              <ColumnsVisibility
                options={filteredOptionsGrouped}
                messageNoOptions={messageNoOptions}
              >
                {(label, groupOptions) => (
                  <SearchGroupList
                    key={label}
                    hasLabel={!!label}
                    items={groupOptions}
                    action={(
                      <Checkbox
                        id={propId ? `${propId}-group-${label}` : `group-${label}`}
                        checked={areAllGroupSelected(groupOptions)}
                        indeterminate={isGroupOptionsIndeterminate(groupOptions)}
                        onChange={() => handleSelectGroup(groupOptions)}
                        size="small"
                        theme="black"
                        label={label ? (
                          <SearchGroupListLabel>{label}</SearchGroupListLabel>
                        ) : undefined}
                      />
                    )}
                  >
                    {(option) => {
                      const optionColor = optionsLabelMap[option.id]?.color;
                      const hasOptionColor = optionColor && optionColor !== 'transparent';
                      return (
                        <SearchListItem key={option.id} id={option.id} isDisabled>
                          <Checkbox
                            id={propId ? `${propId}-option-${option.id}` : `option-${option.id}`}
                            checked={selectedSet.has(String(option.id))}
                            onChange={() => handleToggleOption(option.id)}
                            size="small"
                            theme="black"
                            label={hasOptionColor ? (
                              <span className={styles['option-label']}>
                                <FontAwesomeIcon
                                  icon={faSquare}
                                  className={styles['trigger-color-icon']}
                                  style={{ color: optionColor }}
                                  aria-label={`Couleur: ${optionColor}`}
                                />
                                {option.lib}
                              </span>
                            ) : option.lib}
                          />
                        </SearchListItem>
                      );
                    }}
                  </SearchGroupList>
                )}
              </ColumnsVisibility>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {error ? (
        <div id={propId ? `${propId}-error` : undefined} className={styles.error} role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

export default SelectMultipleCheckboxList;
